import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import cn from "clsx";
import { Icon } from "../icon";

const buttonVariants = cva("in-button", {
  variants: {
    variant: {
      primary: "in-button--primary",
      secondary: "in-button--secondary",
      tertiary: "in-button--tertiary",
      success: "in-button--success",
      danger: "in-button--danger",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

export type ButtonProps = {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  iconPosition?: "left" | "right";
  icon?: string;
  isSolid?: boolean;
} & VariantProps<typeof buttonVariants> &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      children,
      asChild = false,
      isLoading = false,
      loadingText = "Laster...",
      icon,
      iconPosition = "left",
      isSolid = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const ariaLabel = useCorrectAriaLabel(props, isLoading, loadingText);
    const isIconOnly = !children && icon;

    if (isIconOnly && !ariaLabel) {
      throw new Error(
        "If the button has an icon and no text, 'aria-label' is required."
      );
    }

    function getChild() {
      if (React.isValidElement(children)) {
      const firstChild = React.Children.only(children) as React.ReactElement<any>;
      return React.cloneElement(firstChild, {
        children: (
          <ButtonContent
            isLoading={isLoading}
            icon={icon}
            iconPosition={iconPosition}
            isIconOnly={isIconOnly}
            isSolid={isSolid}
            children={firstChild.props.children}
          />
        ),
      });
    }
    }

    return (
      <Comp
        {...(Comp !== "button" ? { role: "button" } : {})}
        {...(Comp === "button" ? { type: props.type ?? "button" } : {})}
        {...(isLoading === true ? { "data-loading": "" } : {})}
        className={cn(buttonVariants({ variant, className }), {
          "in-button--loading": isLoading,
          "in-button--isIconOnly": isIconOnly,
        })}
        ref={ref}
        aria-label={ariaLabel}
        aria-busy={isLoading}
        disabled={props.disabled || isLoading}
        title={isIconOnly ? props.title : undefined}
        {...props}
      >
        {asChild ? (
          getChild()
        ) : (
          <ButtonContent
            isLoading={isLoading}
            icon={icon}
            iconPosition={iconPosition}
            isIconOnly={isIconOnly}
            isSolid={isSolid}
            children={children}
          />
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

const ButtonContent = ({
  isLoading,
  icon,
  iconPosition,
  isIconOnly,
  isSolid,
  children,
}) => {
  return (
    <>
      {isLoading && (
        <div className="in-button__loader">
          <Icon prefix={isSolid ? "fass" : "fasr"} iconName="spinner" />
        </div>
      )}
      <span
        className={cn("in-button__content", {
          "in-button__content--hidden": isLoading,
        })}
      >
        {isIconOnly && iconPosition === undefined && (
          <Icon prefix="fasr" iconName={icon} />
        )}
        {icon && iconPosition === "left" && (
          <Icon prefix={isSolid ? "fass" : "fasr"} iconName={icon} />
        )}
        {!isIconOnly && (
          <span className="in-button__content__text">{children}</span>
        )}
        {icon && iconPosition === "right" && (
          <Icon prefix={isSolid ? "fass" : "fasr"} iconName={icon} />
        )}
      </span>
    </>
  );
};

function useCorrectAriaLabel(
  props: ButtonProps,
  isLoading: boolean,
  loadingText: string
): string {
  if (isLoading) {
    return String(loadingText);
  }
  return props["aria-label"] as string;
}

export { Button, buttonVariants };
